"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Search, ShoppingBag, MapPin, Phone, CheckCircle2 } from "lucide-react";
import { Product } from "../../types/public.types";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import LoginModal from "../LoginModal";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";

interface ProductGridProps {
  products: Product[];
  title?: string;
  searchPlaceholder?: string;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  hasProducts?: boolean;
  emptyMessage?: string;
  emptySubMessage?: string;
  categorySlug?: string;
  children?: React.ReactNode;
}

export default function ProductGrid({
  products,
  title,
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  hasProducts = true,
  emptyMessage = "No products listed",
  emptySubMessage = "There are no approved products in this category at the moment.",
  categorySlug,
  children,
}: ProductGridProps) {
  const router = useRouter();
  const { user, activeRole } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTaluk, setSelectedTaluk] = useState("");

  // Safely extract location fields
  const getLocNames = (prod: Product) => {
    const stateName = typeof prod.location?.state === 'object' ? prod.location.state?.name : prod.location?.state;
    const distName = typeof prod.location?.district === 'object' ? prod.location.district?.name : prod.location?.district || prod.location?.city;
    const talukName = typeof prod.location?.taluk === 'object' ? prod.location.taluk?.name : prod.location?.taluk;
    return {
      stateName: stateName || "",
      distName: distName || "",
      talukName: talukName || "",
    };
  };

  // Get unique districts from all matching products
  const uniqueDistricts = Array.from(
    new Set(
      products
        .map((p) => getLocNames(p).distName)
        .filter(Boolean)
    )
  ).sort() as string[];

  // Get unique taluks for the selected district from matching products
  const uniqueTaluks = Array.from(
    new Set(
      products
        .filter((p) => {
          if (!selectedDistrict) return false;
          const { distName } = getLocNames(p);
          return distName.toLowerCase() === selectedDistrict.toLowerCase();
        })
        .map((p) => getLocNames(p).talukName)
        .filter(Boolean)
    )
  ).sort() as string[];

  // Filter products by location
  const locationFilteredProducts = products.filter((p) => {
    const { distName, talukName } = getLocNames(p);
    
    if (selectedDistrict && distName.toLowerCase() !== selectedDistrict.toLowerCase()) {
      return false;
    }
    if (selectedTaluk && talukName.toLowerCase() !== selectedTaluk.toLowerCase()) {
      return false;
    }
    return true;
  });

  // Reset selected filters if they are no longer in the list of available locations
  React.useEffect(() => {
    if (selectedDistrict && !uniqueDistricts.includes(selectedDistrict)) {
      setSelectedDistrict("");
      setSelectedTaluk("");
    }
  }, [products, selectedDistrict, uniqueDistricts]);

  React.useEffect(() => {
    if (selectedTaluk && !uniqueTaluks.includes(selectedTaluk)) {
      setSelectedTaluk("");
    }
  }, [products, selectedTaluk, uniqueTaluks]);

  const handlePhoneClick = (e: React.MouseEvent) => {
    const isAllowed = user && (activeRole === 'buyer' || activeRole === 'fpo_connect' || user.role === 'buyer' || user.role === 'fpo_connect');
    if (!isAllowed) {
      e.preventDefault();
      e.stopPropagation();
      setIsLoginModalOpen(true);
    } else {
      e.stopPropagation();
    }
  };

  return (
    <div className="space-y-6">
      {/* Grid Header */}
      <div className="flex flex-col gap-4 border-b border-slate-200/60 pb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          {title && (
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-7 bg-emerald-600 rounded-full" />
              <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                {title}
              </h2>
            </div>
          )}

          {/* Search & Location Filters */}
          {hasProducts && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              {/* Search Bar */}
              {onSearchChange && (
                <div className="relative w-full sm:w-60 shrink-0 shadow-xs rounded-xl">
                  <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder || "Search..."}
                    value={searchQuery || ""}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-250 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-600 text-slate-800 font-semibold transition-all duration-200"
                  />
                </div>
              )}

              {/* District Filter */}
              {uniqueDistricts.length > 0 && (
                <div className="relative w-full sm:w-44 shrink-0 shadow-xs rounded-xl">
                  <select
                    value={selectedDistrict}
                    onChange={(e) => {
                      setSelectedDistrict(e.target.value);
                      setSelectedTaluk("");
                    }}
                    className="w-full pl-3 pr-8 py-2.5 border border-slate-250 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-600 text-slate-800 font-semibold transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="">All Districts</option>
                    {uniqueDistricts.map((dist) => (
                      <option key={dist} value={dist}>
                        {dist}
                      </option>
                    ))}
                  </select>
                  <MapPin className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              )}

              {/* Taluk Filter */}
              {selectedDistrict && uniqueTaluks.length > 0 && (
                <div className="relative w-full sm:w-44 shrink-0 shadow-xs rounded-xl">
                  <select
                    value={selectedTaluk}
                    onChange={(e) => setSelectedTaluk(e.target.value)}
                    className="w-full pl-3 pr-8 py-2.5 border border-slate-250 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-600 text-slate-800 font-semibold transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="">All Taluks</option>
                    {uniqueTaluks.map((taluk) => (
                      <option key={taluk} value={taluk}>
                        {taluk}
                      </option>
                    ))}
                  </select>
                  <MapPin className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!hasProducts ? (
        <div className="bg-white p-20 text-center border border-slate-200 rounded-2xl shadow-xs">
          <ShoppingBag className="w-14 h-14 text-slate-350 mx-auto mb-4" />
          <h5 className="font-bold text-slate-800 text-base">{emptyMessage}</h5>
          <p className="text-sm text-slate-500 mt-1">{emptySubMessage}</p>
        </div>
      ) : locationFilteredProducts.length === 0 ? (
        <div className="bg-white p-20 text-center border border-slate-200 rounded-2xl shadow-xs animate-in fade-in duration-300">
          <Search className="w-14 h-14 text-slate-350 mx-auto mb-4" />
          <h5 className="font-bold text-slate-800 text-base">No matching results</h5>
          <p className="text-sm text-slate-500 mt-1">Try resetting or editing your search keyword or location filters.</p>
          {(selectedDistrict || selectedTaluk) && (
            <button
              onClick={() => {
                setSelectedDistrict("");
                setSelectedTaluk("");
              }}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition cursor-pointer"
            >
              Reset Location Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {locationFilteredProducts.map((prod) => {
              // Find the minimum price variant to use as starting price / details
              const activeVariant = prod.variants && prod.variants.length > 0
                ? prod.variants.reduce((min, v) => v.salePrice < min.salePrice ? v : min, prod.variants[0])
                : null;
              const salePrice = activeVariant ? activeVariant.salePrice : null;
              const mrpPrice = activeVariant ? activeVariant.mrpPrice : null;
              const variantName = activeVariant ? activeVariant.variant : "";

              const priceString = salePrice !== null
                ? (variantName ? `₹${salePrice} / ${variantName}` : `₹${salePrice}`)
                : null;

              // Determine URLs dynamically
              const currentCategorySlug = prod.category?.slug || categorySlug || "general";
              const currentSubcategorySlug = prod.subcategory?.slug || "uncategorized";
              const productUrl = `/${currentCategorySlug}/${currentSubcategorySlug}/${prod.slug}`;

              return (
                <div
                  key={prod._id}
                  onClick={() => router.push(productUrl)}
                  className="group bg-white rounded-lg border border-slate-200/85 hover:border-green-300 transition-all duration-300 hover:shadow-lg flex flex-col justify-between p-3 sm:p-4 h-full min-h-[310px] sm:min-h-[350px] w-full max-w-[240px] mx-auto text-center shadow-xs cursor-pointer"
                >
                  <div className="flex flex-col flex-1">
                    {/* Compact Rounded Image Frame */}
                    <div className="relative w-full h-[110px] sm:h-[130px] bg-white rounded-lg overflow-hidden flex items-center justify-center p-2 border border-slate-100 mb-2">
                      {prod.imageUrl ? (
                        <Image
                          src={prod.imageUrl}
                          alt={prod.title}
                          fill
                          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 240px"
                          className="object-contain p-2 transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <ShoppingBag className="w-10 h-10 sm:w-12 sm:h-12 text-slate-350" />
                      )}
                      {prod.subcategory?.name && (
                        <span className="absolute top-2 left-2 bg-[#eafaf1] text-[#0f9648] text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border border-[#c2ecd3] shadow-xs z-10">
                          {prod.subcategory.name}
                        </span>
                      )}
                    </div>

                    {/* Text Content */}
                    <div className="flex flex-col items-center">
                      {/* Title */}
                      <h3 className="text-xs sm:text-sm md:text-base font-bold text-slate-900 group-hover:text-green-600 transition-colors duration-200 line-clamp-1 mb-0.5 sm:mb-1 capitalize px-1">
                        {prod.title}
                      </h3>

                      {/* Vendor Name */}
                      <span className="text-[11px] sm:text-xs md:text-[14px] font-semibold text-slate-500 mb-1 sm:mb-1.5 flex items-center justify-center gap-1 max-w-full">
                        <span className="truncate">{prod.vendorDetails?.name || "Verified Vendor"}</span>
                        {prod.vendorDetails?.isApproved && (
                          <span title="Verified Vendor" className="inline-flex shrink-0">
                            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-800 fill-green-40" />
                          </span>
                        )}
                      </span>

                      {/* Location Details */}
                      {(() => {
                        const { distName, talukName } = getLocNames(prod);
                        const locationText = [talukName, distName].filter(Boolean).join(", ");
                        if (!locationText) return null;
                        return (
                          <div className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-1.5 flex items-center gap-1 max-w-full justify-center">
                            <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-600 shrink-0" />
                            <span className="truncate">{locationText}</span>
                          </div>
                        );
                      })()}

                      {/* Pricing */}
                      {priceString ? (
                        <span className="text-xs sm:text-sm md:text-base font-bold text-green-700 text-center">
                          {priceString}
                        </span>
                      ) : (
                        <span className="text-[10px] sm:text-xs font-bold text-slate-400 italic text-center">
                          Call for Quote
                        </span>
                      )}

                      {/* MRP Cross-Out */}
                      {mrpPrice !== null && mrpPrice > (salePrice || 0) && (
                        <span className="text-[10px] sm:text-xs font-semibold text-red-700 line-through text-center mt-0.5">
                          ₹{mrpPrice}
                        </span>
                      )}

                      {/* Stock Status */}
                      <span className="text-[10px] sm:text-xs text-slate-500 text-center mt-0.5 sm:mt-1">
                        In Stock ({variantName || "1"})
                      </span>
                    </div>
                  </div>

                  {/* Stacked Vertical Action Buttons */}
                  <div className="mt-2.5 sm:mt-3 flex flex-col gap-1.5 sm:gap-2 w-full">
                    {/* View Number Button */}
                    {prod.vendorDetails?.phoneNumber ? (
                      <a
                        href={`tel:${prod.vendorDetails.phoneNumber}`}
                        onClick={handlePhoneClick}
                        className="w-full border border-green-600 text-green-700 font-bold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg hover:bg-green-50 transition flex items-center justify-center gap-1.5 bg-white text-[10px] sm:text-xs cursor-pointer"
                      >
                        <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                        View Number
                      </a>
                    ) : (
                      <span
                        onClick={handlePhoneClick}
                        className="w-full border border-slate-200 text-slate-400 font-bold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg flex items-center justify-center gap-1.5 bg-slate-50 text-[10px] sm:text-xs cursor-pointer"
                      >
                        <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                        View Number
                      </span>
                    )}

                    {/* Get Best Price Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        router.push(productUrl);
                      }}
                      className="w-full bg-[#0b7033] hover:bg-[#074720] text-white font-bold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg transition duration-200 text-[10px] sm:text-xs text-center block cursor-pointer"
                    >
                      Get Best Price
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {children}
        </>
      )}

      {/* Reusable Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </div>
  );
}
